import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	CircularProgress,
	Button,
	Paper,
} from "@material-ui/core";
import { CloudUpload } from "@material-ui/icons";
import { DropzoneArea } from "material-ui-dropzone";
import { DisplayMessage } from "../../../../services/helpers";
import { useMediaUploaderStyles } from "../utils/styles";
import MediaUploadService from "../services/MediaUploadService";
import { makeStyles } from "@material-ui/core/styles";
import { InfoPanel } from "../../Panels/InfoPanel";

const MAX_FILE_SIZE = 10000000; // 10MB

const ACCEPTED_FILE_TYPES = [
	"image/jpeg",
	"image/png",
	"audio/wav",
	"audio/mp3",
	"video/mp4",
	"video/mov",
	"video/quicktime",
	"audio/mpeg",
	"application/pdf",
];

const useRemoveButtonStyles = makeStyles({
	"@global": {
		".media-uploader-instance .MuiDropzonePreviewList-removeButton": {
			top: "8px !important",
			right: "8px !important",
		},
	},
});

const MediaUploader = ({ onUpload, uploading, resources, module, style }) => {
	const [panel, setPanel] = useState({
		show: false,
		severity: undefined,
		message: "",
	});
	const mainClasses = useMediaUploaderStyles();
	useRemoveButtonStyles();
	const [files, setFiles] = useState([]);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadService, setUploadService] = useState(null);
	const [dropzoneKey, setDropzoneKey] = useState(0);

	useEffect(() => {
		setUploadService(new MediaUploadService(module, resources));
	}, [module, resources]);

	const handleFilesChange = (files) => {
		setFiles(files);
		setPanel({ show: false, severity: undefined, message: "" });
	};

	const handleDropRejected = (rejectedFiles) => {
		rejectedFiles.forEach((file) => {
			if (file.size > MAX_FILE_SIZE) {
				DisplayMessage(resources["FileTooLarge"], "error");
			} else if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
				DisplayMessage(resources["InvalidFileType"], "error");
			} else {
				DisplayMessage(resources["FileUploadFailed"], "error");
			}
		});
	};

	const showPanel = (severity, message, duration = 3000) => {
		setPanel({ show: true, severity, message });
		setTimeout(() => {
			setPanel((prev) => ({ ...prev, show: false }));
		}, duration);
	};

	const handleUpload = async () => {
		if (files.length === 0 || !uploadService) return;

		setIsUploading(true);
		try {
			const successfulUploads = await uploadService.uploadFiles(files);

			if (successfulUploads.length === 0) {
				DisplayMessage(resources["NoFilesUploaded"], "error");
				showPanel("danger", resources["MediaUploadFailed"]);
				return;
			}

			const success = await onUpload(successfulUploads);

			if (success) {
				setFiles([]);
				showPanel("success", resources["MediaUploadedSuccessfully"]);
			} else {
				showPanel("danger", resources["MediaUploadFailed"]);
			}
		} catch (e) {
			showPanel("danger", resources["MediaUploadFailed"]);
		} finally {
			setIsUploading(false);
			window.setTimeout(() => {
				setDropzoneKey((prevKey) => prevKey + 1);
			}, 0);
		}
	};

	return (
		<Paper style={style} className={mainClasses.root}>
			<Typography variant="h6" gutterBottom>
				{resources["UploadMedia"]}
			</Typography>

			<Typography
				variant="body2"
				color="textSecondary"
				style={{ marginBottom: 8 }}
			>
				{resources["SupportedFormats"]} JPG, JPEG, PNG, WAV, MP3, MP4, MOV,
				QuickTime, PDF ({resources["Max10Files"]})
			</Typography>
			<div className="media-uploader-instance">
				<DropzoneArea
					key={`dropzone-${dropzoneKey}`}
					acceptedFiles={ACCEPTED_FILE_TYPES}
					dropzoneText={resources["SelectOrDragAndDropFiles"]}
					onChange={handleFilesChange}
					onDropRejected={handleDropRejected}
					filesLimit={10}
					maxFileSize={MAX_FILE_SIZE}
					showPreviewsInDropzone={true}
					showFileNamesInPreview={false}
					showAlerts={false}
					className={mainClasses.dropzone}
					dropzoneClass="dropzone-mediaupload"
					previewGridProps={{
						container: {
							spacing: 2,
							style: {
								width: "100%",
							},
						},
						item: {
							xs: false,
							style: { marginRight: 0, minWidth: 0 },
						},
					}}
					clearOnUnmount={true}
				/>
			</div>

			<Box className={mainClasses.buttonContainer}>
				<InfoPanel
					showTransition={panel.show}
					severity={panel.severity}
					message={panel.message}
					style={{ marginTop: 0, padding: 6 }}
				/>
				<Button
					variant="contained"
					color="primary"
					startIcon={
						isUploading || uploading ? (
							<CircularProgress size={20} color="inherit" />
						) : (
							<CloudUpload />
						)
					}
					onClick={handleUpload}
					disabled={files.length === 0 || isUploading || uploading}
					className={mainClasses.uploadButton}
					id="media-uploader-upload-button"
				>
					{isUploading || uploading
						? resources["Uploading"]
						: resources["UploadFiles"]}
				</Button>
			</Box>
		</Paper>
	);
};

export default MediaUploader;
